import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';
import LocalizationManager from '../../../utils/localization/localization';

const ModernContactUs = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [contentError, setContentError] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);

  const createSessionId = () => {
    const sessionId = Math.random().toString(36).substring(2);
    const timestamp = moment().unix();
    localStorage.setItem('sessionId', sessionId);
    localStorage.setItem('lastEmailSent', timestamp.toString());
    return sessionId;
  };

  const canSendEmail = () => {
    const lastSentStr = localStorage.getItem('lastEmailSent');
    if (lastSentStr === null) {
      return true;
    }
    const lastSent = moment.unix(parseInt(lastSentStr, 10));
    const oneHourAgo = moment().subtract(1, 'hour');
    return lastSent.isBefore(oneHourAgo);
  };

  const verifyEmailData = () => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    const regex = new RegExp(emailRegex);
    if (!regex.test(email)) {
      setEmailError(true);
      return;
    }
    if (message.length < 20) {
      setContentError(true);
      return;
    }

    if (!canSendEmail()) {
      setSending(true);
      setTimeout(() => {
        setSending(false);
        clearForm();
      }, 5000);
      return;
    }

    setSending(true);
    let sessionId = localStorage.getItem('sessionId');
    if (sessionId === null) {
      sessionId = createSessionId();
    }
    void sendEmail(sessionId);
  };

  const clearForm = () => {
    setEmail('');
    setMessage('');
    setSending(false);
  };

  const sendEmail = async (sessionId: string) => {
    try {
      const payload = { email, message, sessionId };
      await axios.post(
        'https://us-central1-gdlestore-6c1d2.cloudfunctions.net/contact/sendMessage',
        payload
      );
      clearForm();
    } catch (error) {
      clearForm();
    }
  };

  const setValueFactory =
    (setter: {
      (value: React.SetStateAction<string>): void;
      (value: React.SetStateAction<string>): void;
      (arg0: string): void;
    }) =>
    (event: { target: { value: string } }) => {
      setter(event.target.value);
      setEmailError(false);
      setContentError(false);
    };

  return (
    <div className="relative min-h-screen bg-dark-950 overflow-hidden pt-20 pb-20 px-4 sm:px-6 lg:px-8">
      {/* Animated gradient background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-pink-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Header section */}
        <div className="text-center mb-16 animate-slideUp">
          <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6">
            {LocalizationManager.t('leaveUsAMessage')}
          </h1>
          <p className="text-xl text-dark-300 max-w-2xl mx-auto leading-relaxed">
            {LocalizationManager.t('contactFormSubtitle')}
          </p>
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Contact Information */}
          <div className="space-y-6 animate-slideUp">
            <div className="glass p-8 rounded-2xl backdrop-blur-xl">
              <h2 className="text-2xl font-bold text-white mb-6">{LocalizationManager.t('contactGetInTouch')}</h2>
              <div className="space-y-6">
                {/* Email */}
                <div>
                  <p className="text-sm font-semibold text-purple-400 mb-2">{LocalizationManager.t('contactEmail')}</p>
                  <a
                    href="mailto:info@gdlestore.com"
                    className="text-dark-300 hover:text-white transition-colors duration-300"
                  >
                    info@gdlestore.com
                  </a>
                </div>

                {/* Phone */}
                <div>
                  <p className="text-sm font-semibold text-purple-400 mb-2">{LocalizationManager.t('contactPhone')}</p>
                  <a
                    href="tel:+523321814488"
                    className="text-dark-300 hover:text-white transition-colors duration-300"
                  >
                    +52 332 181 4488
                  </a>
                </div>

                {/* WhatsApp */}
                <div>
                  <p className="text-sm font-semibold text-purple-400 mb-2">{LocalizationManager.t('contactWhatsApp')}</p>
                  <a
                    href="https://wa.me/523321814488"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-dark-300 hover:text-white transition-colors duration-300"
                  >
                    +52 332 181 4488
                  </a>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="glass p-8 rounded-2xl backdrop-blur-xl">
              <h3 className="text-lg font-bold text-white mb-4">{LocalizationManager.t('contactFollowUs')}</h3>
              <div className="flex gap-4">
                {[
                  { abbr: 'f', name: 'Facebook', url: 'https://www.facebook.com/gdlestore' },
                  { abbr: 'x', name: 'Twitter', url: 'https://www.twitter.com/gdlestore' },
                  { abbr: 'in', name: 'Instagram', url: 'https://www.instagram.com/gdlestore' },
                  { abbr: 'gh', name: 'GitHub', url: 'https://github.com/gdlestore' }
                ].map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 glass rounded-full flex items-center justify-center text-white text-sm font-bold hover:backdrop-blur-2xl hover:bg-white hover:bg-opacity-20 transition-all duration-300"
                    title={social.name}
                  >
                    {social.abbr}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="animate-slideUp" style={{ animationDelay: '100ms' }}>
            <div className="glass p-8 rounded-2xl backdrop-blur-xl">
              <form action="#" className="space-y-6">
                {/* Email Input */}
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    {LocalizationManager.t('whatIsYourEmail')}
                  </label>
                  {emailError && (
                    <p className="text-red-400 text-xs mb-2">{LocalizationManager.t('invalidEmail')}</p>
                  )}
                  <input
                    type="email"
                    value={email}
                    onChange={setValueFactory(setEmail)}
                    disabled={sending}
                    required
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-dark-400 focus:outline-none focus:border-purple-400 focus:border-opacity-50 transition-all duration-300 disabled:opacity-50"
                  />
                </div>

                {/* Message Input */}
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    {LocalizationManager.t('whatIsYourMessage')}
                  </label>
                  {contentError && (
                    <p className="text-red-400 text-xs mb-2">{LocalizationManager.t('invalidContent')}</p>
                  )}
                  <textarea
                    value={message}
                    onChange={setValueFactory(setMessage)}
                    disabled={sending}
                    required
                    placeholder="Tell us more about your project..."
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-dark-400 focus:outline-none focus:border-purple-400 focus:border-opacity-50 transition-all duration-300 resize-none disabled:opacity-50"
                  />
                </div>

                {/* Submit Button */}
                {!sending ? (
                  <button
                    type="submit"
                    onClick={verifyEmailData}
                    disabled={sending}
                    className="w-full group relative inline-flex items-center justify-center px-8 py-4 font-semibold text-white overflow-hidden rounded-xl transition-all duration-300"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 group-hover:shadow-2xl group-hover:shadow-purple-500/50 transition-all duration-300"></div>
                    <span className="relative">{LocalizationManager.t('send')}</span>
                  </button>
                ) : (
                  <div className="w-full flex items-center justify-center py-4">
                    <div className="relative w-8 h-8">
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full animate-spin"></div>
                      <div className="absolute inset-2 bg-dark-950 rounded-full"></div>
                    </div>
                  </div>
                )}
              </form>

              {/* Footer note */}
              <div className="mt-8 pt-6 border-t border-white border-opacity-10">
                <p className="text-dark-400 text-sm text-center">
                  {LocalizationManager.t('contactResponseTime')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Back to home link */}
        <div className="text-center mt-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-dark-300 hover:text-white transition-colors duration-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {LocalizationManager.t('contactBackToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ModernContactUs;
